// Compact account card: identity and controls share a single row.
// Only verification appears inline; full names are available on hover/focus
// and in the profile popover (including on touch devices).
//
// Behavior:
//
//   - The avatar+name region is the trigger for the
//     `ProfilePopover`. Clicking it opens the popover anchored to
//     the right of the card.
//   - The mic and deafen buttons toggle visual state from
//     `useSelf`. When active they switch to a strawberry tint and
//     the icon swaps to its "off" variant.
//   - Clicking the gear icon opens the full profile modal (the
//     status picker / themes flow lives there).

import {
  Headphones,
  HeadphoneOff,
  Mic,
  MicOff,
  Settings,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  GlassMaterial,
  Popover,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@link/ui";

import { ProfilePopover } from "./ProfilePopover";
import { BadgeRow } from "./BadgeRow";
import { verifiedBadgeOnly } from "../lib/badges";
import { STATUS_LABEL, StatusDot } from "./StatusDot";
import { useSelf, visibleStatus } from "../state/SelfSession";

export function UserCard() {
  const self = useSelf();
  const dotStatus = visibleStatus(self.status);
  const [compactNavigation, setCompactNavigation] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 1023px)").matches
      : false,
  );

  useEffect(() => {
    const query = window.matchMedia("(max-width: 1023px)");
    const update = (): void => setCompactNavigation(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  return (
    <section aria-label="Your account" className="m-2 shrink-0">
      <div className="liquid-lens-host relative flex items-center gap-1 rounded-xl border border-cloud-200 bg-white p-1 shadow-sm">
        <GlassMaterial radius={12} />
        <Popover>
          <Tooltip delayDuration={250}>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="group flex min-w-0 flex-1 items-center gap-2 rounded-lg px-1 py-1.5 text-left transition-colors hover:bg-cloud-200/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-strawberry-400"
                  aria-label={`Open profile for ${self.identity.name}, @${self.identity.username}`}
                >
                  <div className="relative flex-shrink-0">
                    <Avatar className="h-8 w-8">
                      {self.identity.avatarUrl !== null ? (
                        <AvatarImage src={self.identity.avatarUrl} alt="" />
                      ) : null}
                      <AvatarFallback className="text-sm font-semibold uppercase">
                        {self.identity.name.slice(0, 1)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="absolute -bottom-0.5 -right-0.5 rounded-full bg-white p-0.5">
                      <StatusDot
                        status={dotStatus}
                        sizeClass="h-2.5 w-2.5"
                        ringClass="ring-white"
                      />
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-1">
                      <p className="min-w-0 flex-1 line-clamp-2 whitespace-normal text-[13px] font-semibold leading-tight text-cloud-700 [overflow-wrap:anywhere] group-hover:text-cloud-900">
                        <span>{self.identity.name}</span>
                      </p>
                      <BadgeRow
                        badges={verifiedBadgeOnly(self.identity.badges ?? [])}
                        className="mt-0.5 shrink-0"
                      />
                    </div>
                    <p className="mt-0.5 text-[10px] font-medium leading-tight text-cloud-500">
                      {self.status === "invisible" ? "Invisible" : STATUS_LABEL[dotStatus]}
                    </p>
                  </div>
                </button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              align="start"
              collisionPadding={12}
              className="max-w-[min(20rem,calc(100vw-1.5rem))] whitespace-normal [overflow-wrap:anywhere]"
            >
              <p className="font-semibold">{self.identity.name}</p>
              <p className="mt-0.5 font-normal">@{self.identity.username}</p>
            </TooltipContent>
          </Tooltip>
          <ProfilePopover
            side={compactNavigation ? "top" : "right"}
            align="end"
            onOpenFullProfile={() => self.setProfileOpen(true)}
          />
        </Popover>

        <div role="group" aria-label="Account controls" className="flex shrink-0 items-center gap-0.5 text-cloud-500">
          <ToggleAction
            active={self.micMuted}
            activeTooltip="Unmute mic"
            inactiveTooltip="Mute mic"
            activeColor="bg-red-100 text-red-600 hover:bg-red-200"
            inactiveColor="hover:bg-strawberry-50 hover:text-strawberry-600"
            onClick={self.toggleMic}
            activeIcon={<MicOff className="h-4 w-4" />}
            inactiveIcon={<Mic className="h-4 w-4" />}
          />
          <ToggleAction
            active={self.deafened}
            activeTooltip="Undeafen"
            inactiveTooltip="Deafen"
            activeColor="bg-red-100 text-red-600 hover:bg-red-200"
            inactiveColor="hover:bg-strawberry-50 hover:text-strawberry-600"
            onClick={self.toggleDeafen}
            activeIcon={<HeadphoneOff className="h-4 w-4" />}
            inactiveIcon={<Headphones className="h-4 w-4" />}
          />
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="User settings"
                onClick={() => self.setSettingsOpen(true)}
                className="ui-glass-icon flex h-8 w-8 items-center justify-center rounded-md hover:bg-strawberry-50 hover:text-strawberry-600"
              >
                <Settings className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>User settings</TooltipContent>
          </Tooltip>
        </div>
      </div>
    </section>
  );
}

interface ToggleActionProps {
  active: boolean;
  activeTooltip: string;
  inactiveTooltip: string;
  activeColor: string;
  inactiveColor: string;
  activeIcon: ReactNode;
  inactiveIcon: ReactNode;
  onClick: () => void;
}

function ToggleAction({
  active,
  activeTooltip,
  inactiveTooltip,
  activeColor,
  inactiveColor,
  activeIcon,
  inactiveIcon,
  onClick,
}: ToggleActionProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          className={cn(
            "ui-glass-icon flex h-8 w-8 items-center justify-center rounded-md transition-colors",
            active ? activeColor : inactiveColor,
          )}
          aria-pressed={active}
          aria-label={active ? activeTooltip : inactiveTooltip}
        >
          {active ? activeIcon : inactiveIcon}
        </button>
      </TooltipTrigger>
      <TooltipContent>{active ? activeTooltip : inactiveTooltip}</TooltipContent>
    </Tooltip>
  );
}
